import { useCallback, useEffect, useRef, useState } from "react";
import VendorIntegrationCard from "./VendorIntegrationCard";
import Turn14PartnerCallout from "./Turn14PartnerCallout";
import agent from "../../app/api/agent";
import { fieldChecked, fieldValue, useModalOpen } from "../../app/components/polaris";
import type { Turn14IntegrationSettings as Turn14IntegrationSettingsModel } from "../../app/models/Turn14Integration";

export type IntegrationEditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type Turn14IntegrationSettingsProps = {
    onStatusChange?: (status: IntegrationEditorStatus) => void;
    onOpenFitmentDataSources?: () => void;
    display?: "card" | "button";
    disabled?: boolean;
    fitmentDataEnabled?: boolean;
    onSaved?: (settings: Turn14IntegrationSettingsModel) => void;
    autoOpen?: boolean;
    onAutoOpened?: () => void;
};

const createEmptySettings = (): Turn14IntegrationSettingsModel => ({
    accountId: null,
    accountName: "",
    clientId: "",
    clientSecret: "",
    hasClientSecret: false,
    isConfigured: false,
    isActive: false,
    fitmentDataEnabled: false,
    fitmentDataAvailable: false,
});

const normalizeSettings = (settings?: Partial<Turn14IntegrationSettingsModel> | null): Turn14IntegrationSettingsModel => ({
    ...createEmptySettings(),
    ...(settings ?? {}),
    clientSecret: "",
});

const Turn14IntegrationSettings: React.FC<Turn14IntegrationSettingsProps> = ({
    onStatusChange,
    onOpenFitmentDataSources,
    display = "card",
    disabled = false,
    fitmentDataEnabled,
    onSaved,
    autoOpen = false,
    onAutoOpened,
}) => {
    const autoOpenHandled = useRef(false);
    const [settings, setSettings] = useState<Turn14IntegrationSettingsModel>();
    const [modalDraft, setModalDraft] = useState<Turn14IntegrationSettingsModel>();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();

    const loadSettings = useCallback(async () => {
        setLoading(true);
        setError(undefined);
        setSuccess(undefined);
        try {
            setSettings(normalizeSettings(await agent.Settings.turn14Integration()));
        } catch {
            setError("Unable to load Turn14 connection. Try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void loadSettings(); }, [loadSettings]);

    useEffect(() => {
        onStatusChange?.({
            // Credentials are saved inside the modal, not through the global
            // settings save bar. Keep parent save/discard state clean so future
            // integrations can follow the same compact-card pattern.
            hasChanges: false,
            loading,
            saving,
        });
    }, [loading, onStatusChange, saving]);

    const openModal = useCallback(() => {
        setModalDraft({
            ...normalizeSettings(settings),
            fitmentDataEnabled: fitmentDataEnabled ?? settings?.fitmentDataEnabled ?? false,
        });
        setError(undefined);
        setSuccess(undefined);
        setModalOpen(true);
    }, [fitmentDataEnabled, settings]);

    useEffect(() => {
        if (!autoOpen) {
            autoOpenHandled.current = false;
            return;
        }
        if (autoOpenHandled.current || loading || !settings || disabled) return;
        autoOpenHandled.current = true;
        openModal();
        onAutoOpened?.();
    }, [autoOpen, disabled, loading, onAutoOpened, openModal, settings]);

    const closeModal = useCallback(() => {
        if (saving || testing) return;

        setModalOpen(false);
        setModalDraft(undefined);
        setError(undefined);
        setSuccess(undefined);
    }, [saving, testing]);

    const updateDraft = useCallback((changes: Partial<Turn14IntegrationSettingsModel>) => {
        setError(undefined);
        setSuccess(undefined);
        setModalDraft((current) => current ? ({ ...current, ...changes }) : current);
    }, []);

    const validate = useCallback(() => {
        if (!modalDraft) return "Turn14 settings are not loaded.";
        if (!modalDraft.clientId.trim()) return "Turn14 client ID is required.";

        // Saved secrets are intentionally write-only. A blank secret preserves
        // the stored value; new credentials need the secret before save/test.
        if (!modalDraft.clientSecret.trim() && !modalDraft.hasClientSecret) {
            return "Turn14 client secret is required.";
        }

        return undefined;
    }, [modalDraft]);

    const saveChanges = useCallback(async () => {
        const validationError = validate();
        if (validationError || !modalDraft) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            const saved = normalizeSettings(await agent.Settings.updateTurn14Integration({
                ...modalDraft,
                accountName: "",
                clientId: modalDraft.clientId.trim(),
                clientSecret: modalDraft.clientSecret.trim(),
            }));
            setSettings(saved);
            setModalDraft(saved);
            setModalOpen(false);
            window.shopify?.toast?.show?.("Turn14 connection saved");
            onSaved?.(saved);
        } catch (err: any) {
            setError(err?.response?.data ?? err?.message ?? "Failed to save Turn14 integration settings.");
        } finally {
            setSaving(false);
        }
    }, [modalDraft, onSaved, validate]);

    const testConnection = useCallback(async () => {
        const validationError = validate();
        if (validationError || !modalDraft) {
            setError(validationError);
            return;
        }

        setTesting(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            const result = await agent.Settings.testTurn14Integration({
                ...modalDraft,
                clientId: modalDraft.clientId.trim(),
                clientSecret: modalDraft.clientSecret.trim(),
            });

            if (result.success) {
                setSuccess(result.message || "Turn14 connection succeeded.");
            } else {
                setError(result.message || "Turn14 connection failed.");
            }
        } catch (err: any) {
            setError(err?.response?.data ?? err?.message ?? "Failed to test Turn14 connection.");
        } finally {
            setTesting(false);
        }
    }, [modalDraft, validate]);

    const modalRef = useModalOpen(modalOpen, closeModal);
    const actionLabel = loading ? "Checking…" : !settings ? "Retry connection check" : settings.isConfigured ? "Manage connection" : "Connect Turn14";
    const onAction = !settings ? loadSettings : openModal;

    return (
        <>
            {display === "button" ? (
                <s-stack gap="small-200">
                    <s-stack direction="inline">
                        <s-button variant={settings?.isConfigured ? "secondary" : "primary"} disabled={disabled || loading}
                            onClick={onAction}>{actionLabel}</s-button>
                    </s-stack>
                    {!loading && !settings && error && <s-text tone="critical">{error}</s-text>}
                </s-stack>
            ) : <VendorIntegrationCard
                title="Turn14"
                status={loading ? "loading" : !settings ? "error" : settings.isConfigured ? "connected" : "disconnected"}
                detail={!settings ? (loading ? undefined : error || "Unable to load Turn14 connection.")
                    : settings.isConfigured ? (settings.fitmentDataEnabled ? "Fitment data is on" : "Fitment data is off") : undefined}
                action={{ label: actionLabel, onAction }}
                configuration={onOpenFitmentDataSources ? { label: "Configure fitment data", onAction: onOpenFitmentDataSources } : undefined}
            >
                <Turn14PartnerCallout />
            </VendorIntegrationCard>}

            <s-modal ref={modalRef} id="turn14-integration-modal" heading="Turn14">
                {/* s-modal keeps its children mounted; render the form only while open. */}
                {modalOpen && modalDraft && <s-stack gap="base">
                    {error && <s-banner tone="critical">{error}</s-banner>}
                    {success && <s-banner tone="success">{success}</s-banner>}
                    <s-paragraph>
                        Find your API credentials in <s-link href="https://turn14.com" target="_blank">Turn14</s-link> under Settings &amp; Data → API.
                    </s-paragraph>
                    <s-text-field label="Client ID" autocomplete="off" maxLength={100} value={modalDraft.clientId}
                        onInput={event => updateDraft({ clientId: fieldValue(event) })} />
                    <s-password-field label="Client secret" autocomplete="new-password" maxLength={200}
                        value={modalDraft.clientSecret} placeholder={modalDraft.hasClientSecret ? "Saved credential" : undefined}
                        onInput={event => updateDraft({ clientSecret: fieldValue(event) })} />
                    <s-checkbox label="Use Turn14 fitment data" checked={modalDraft.fitmentDataEnabled}
                        onChange={event => updateDraft({ fitmentDataEnabled: fieldChecked(event) })} />
                </s-stack>}
                <s-button slot="primary-action" variant="primary" onClick={saveChanges} loading={saving}
                    disabled={saving || testing}>Save</s-button>
                <s-button slot="secondary-actions" onClick={testConnection} loading={testing}
                    disabled={saving || testing}>Test connection</s-button>
                <s-button slot="secondary-actions" onClick={closeModal} disabled={saving || testing}>Cancel</s-button>
            </s-modal>
        </>
    );
};

export default Turn14IntegrationSettings;
